import { createReducer, on } from '@ngrx/store';

import { AttachmentResponse, IssueResponse } from '@core/api/models';
import * as objectActions from '@core/api/object.actions';
import { actionOpenObjectFromMap } from '@modules/map/store/map.actions';
import {
  actionOpenObjectFromProfile,
  actionOpenTableFromProfile,
} from '@modules/profile';
import {
  actionOpenObjectFromSearch,
  actionTableGetWithResults,
} from '@modules/spatial-search/store/spatial-search.actions';

import {
  actionAddTempInline,
  actionCancelEditing,
  actionDeleteTempInline,
  actionEditObject,
  actionObjectNavigateBack,
} from '../object-panel/object-panel.actions';
import {
  actionAddNewObject,
  actionOpenObjectEditing,
  actionOpenObjectFromTable,
  actionOpenReportSuccess,
  actionTableClose,
  actionTableGet,
} from './map-panel.actions';

export const mapPanelFeatureKey = 'map-panel';

export enum RowsData {
  GEOMETRY = 'GEOMETRY',
  DATA = 'DATA',
  RESULTS = 'RESULTS',
}

export enum PanelType {
  TABLE = 'TABLE',
  OBJECT = 'OBJECT',
}
export interface MapPanelState {
  type: PanelType | null;
  isEditing: boolean;
  isNew: boolean;
  isReport: string;
  rowsData: RowsData;
  next: string;
  tempAttachments: FormData[];
  tempIssues: TempIssue[];
}

export interface TempIssue {
  issue: IssueResponse;
  value: IssueResponse;
}

export interface TempAttachment {
  attachment: AttachmentResponse;
  formData: FormData;
}

const initialState: MapPanelState = {
  isEditing: false,
  isNew: false,
  isReport: null,
  next: null,
  rowsData: RowsData.DATA,
  tempAttachments: [],
  tempIssues: [],
  type: null,
};

export const mapPanelReducer = createReducer(
  initialState,
  on(
    actionObjectNavigateBack,
    objectActions.actionObjectUpdateSuccess,
    objectActions.actionObjectInlineAddSuccess,
    actionOpenObjectFromMap,
    actionOpenObjectFromTable,
    objectActions.actionObjectGetData,
    actionOpenObjectFromSearch,
    actionOpenObjectFromProfile,
    actionOpenTableFromProfile,
    actionCancelEditing,
    actionTableClose,
    actionTableGet,
    actionTableGetWithResults,
    (state): MapPanelState => ({
      ...state,
      isEditing: false,
      isNew: false,
      isReport: null,
      tempAttachments: initialState.tempAttachments,
    }),
  ),
  on(
    actionEditObject,
    actionAddNewObject,
    actionOpenObjectEditing,
    (state): MapPanelState => ({
      ...state,
      isEditing: true,
      isReport: null,
    }),
  ),
  on(
    actionAddNewObject,
    (state): MapPanelState => ({
      ...state,
      isNew: true,
    }),
  ),
  on(
    actionOpenObjectFromMap,
    actionOpenObjectFromTable,
    actionOpenObjectFromSearch,
    actionOpenObjectFromProfile,
    (state): MapPanelState => ({
      ...state,
      type: PanelType.OBJECT,
    }),
  ),
  on(
    actionOpenTableFromProfile,
    actionObjectNavigateBack,
    (state): MapPanelState => ({
      ...state,
      type: PanelType.TABLE,
    }),
  ),
  on(actionAddTempInline, (state, action) => ({
    ...state,
    tempAttachments: [
      ...state.tempAttachments.filter(
        (t) => t.get('tempId') !== action.data.get('tempId'),
      ),
      action.data,
    ],
  })),
  on(actionDeleteTempInline, (state, action) => ({
    ...state,
    tempAttachments: state.tempAttachments.filter(
      (t) => t.get('tempId') !== action.data.get('tempId'),
    ),
  })),
  on(
    actionTableGetWithResults,
    (state): MapPanelState => ({
      ...state,
      rowsData: RowsData.RESULTS,
    }),
  ),
  on(
    actionTableGet,
    (state): MapPanelState => ({
      ...state,
      rowsData: RowsData.DATA,
      type: PanelType.TABLE,
    }),
  ),
  on(
    actionOpenReportSuccess,
    (state, { payload }): MapPanelState => ({
      ...state,
      isReport: payload.metadata.name,
    }),
  ),
);
